import React, { useState } from 'react';
import { action, observable, makeObservable } from 'mobx';
import { observer } from 'src/utils/mobx-react';
import { PaymentMethodDropdownState } from 'src/domains/players/webview/components/WithdrawAndDeposit/depositProcedure/topUpProcedureParts/PaymentMethodDropdown.state';
import { WithdrawIssue } from 'src/domains/players/webview/components/WithdrawAndDeposit/withdrawProcedure/withdrawProcedureParts/withdrawIssue/WithdrawIssue';
import { PendingWithdrawals, WithdrawProcedureWrapper } from 'src/domains/players/webview/components/Account';
import { WithdrawSuccess } from 'src/domains/players/webview/components/WithdrawAndDeposit/withdrawProcedure/withdrawProcedureParts/withdrawSuccess/WithdrawSuccess';
import { WithdrawWorldpayProcedure } from './WithdrawWorldpayProcedure';
import { WithdrawWorldpayFormState } from './WithdrawWorldpayFormState';
import { Amount } from 'src_common/common/amount/Amount';
import { useCommon } from 'src/domains/common/Common';
import { GoogleTagManagerState } from 'src/domains/layouts/state/googleState/GoogleTagManagerState';
import { LanguagesState } from 'src/domains/layouts/state/languagesState/LanguagesState';
import { ConfigComponents } from 'src/domains/layouts/config/features/config';
import { BasicDataModel } from 'src/domains/players/state/BasicDataModel';
import { UsersState } from 'src/domains/players/state/UsersState';
import { WithdrawalsListState } from 'src/domains/players/state/WithdrawalsListState';

export type WithdrawWorldpayStepsType =
    | { type: 'set-card' }
    | { type: 'failure-view'; readonly failureType: WithdrawWorldpayFailureType }
    | { type: 'withdraw-pending' };

export type WithdrawWorldpayFailureType = 'serverIssue' | 'noPaymentMethods';

const defaultWithdrawStep: WithdrawWorldpayStepsType = { type: 'set-card' };

export class WithdrawWorldpaySteps {
    @observable.ref public step: WithdrawWorldpayStepsType;

    public constructor(public initialStep?: WithdrawWorldpayStepsType) {
        makeObservable(this);
        this.step = initialStep ?? defaultWithdrawStep;
    }

    @action public redirectToSetCard = (): void => {
        this.step = { type: 'set-card' };
    };

    @action public redirectToFailureView = (failureType: WithdrawWorldpayFailureType): void => {
        this.step = { type: 'failure-view', failureType };
    };

    @action public redirectToPending = (): void => {
        this.step = { type: 'withdraw-pending' };
    };
}

export const WithdrawWorldpayJourney = observer('WithdrawWorldpayJourney', () => {
    const common = useCommon();
    const withdrawalsListState = WithdrawalsListState.get(common);
    const usersState = UsersState.get(common);
    const googleTagManager = GoogleTagManagerState.get(common);
    const languagesState = LanguagesState.get(common);
    const basicDataModel = BasicDataModel.get(common);
    const {
        config: { minWithdrawAmount },
    } = ConfigComponents.get(common);

    // Apple Pay can stay on Paysafe while cards use Worldpay: load both and route by the selected method.
    const hasPaysafeApplePay = common.features.paymentProviders.applePay?.type === 'paysafe';
    const [paymentMethodDropdownState] = useState(
        () =>
            new PaymentMethodDropdownState(
                common.trpcClient,
                basicDataModel,
                common,
                hasPaysafeApplePay ? ['worldpay', 'paysafe'] : ['worldpay']
            )
    );

    const [state] = React.useState(
        () =>
            new WithdrawWorldpayFormState(
                common.trpcClient,
                usersState,
                languagesState,
                withdrawalsListState,
                googleTagManager,
                paymentMethodDropdownState,
                new Amount(minWithdrawAmount),
                basicDataModel,
                common
            )
    );

    switch (state.currentStep.type) {
        case 'set-card':
            return (
                <>
                    <WithdrawProcedureWrapper data-test='withdraw-worldpay-procedure-wrapper'>
                        <WithdrawWorldpayProcedure
                            state={state}
                            paymentMethodDropdownState={paymentMethodDropdownState}
                        />
                    </WithdrawProcedureWrapper>
                    <PendingWithdrawals />
                </>
            );
        case 'failure-view':
            return <WithdrawIssue failureType={state.currentStep.failureType} />;
        case 'withdraw-pending':
            return <WithdrawSuccess />;
    }
});
